import { type SetStateAction, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';

import { LoadingGif } from 'components';
import { useAppDispatch, useAppSelector } from 'hooks/storeHooks';
import { useSupportEmail } from 'hooks/useSupportEmail';
import { IError } from 'interfaces/IError';
import { IProfileData } from 'interfaces/IProfile';
import {
  createFailedMessage,
  registration,
  resetAuthError,
  setLoginStatus,
} from 'store/actions';
import { userApi, useVerifyEmailMutation } from 'store/services/user';
import translate from 'translate';
import {
  LoginWaitUserEmail,
  LoginWaitEmailVerification,
  LoginAccountMigrationPassword,
  LoginAccountAgentInfo,
  LOGIN_STATUSES,
} from 'containers/Login';
import { useAuthMutation } from 'store/services/auth';
import { IAuthPayload } from 'interfaces/IAuth';
import { hasValidToken } from 'utils/getToken';
import styles from './styles.module.scss';

const Login = () => {
  const [validatedEmail, setValidatedEmail] = useState<string>('');
  const [password, setPassword] = useState<string>(null);

  const { error: registrationError } = useAppSelector(
    (state) => state.registration
  );
  const { loginStatus } = useAppSelector((state) => state.auth);
  const { registrationResponse } = useAppSelector(
    (state) => state.registration
  );

  const supportEmail = useSupportEmail();

  const dispatch = useAppDispatch();
  const { pathname, state } = useLocation();
  const { token } = useParams();
  const navigate = useNavigate();
  const [auth] = useAuthMutation();

  const isAuthenticated = hasValidToken();

  const [
    validateTokenForAccountMigration,
    { isFetching, isSuccess, isError, error, data },
  ] = userApi.useLazyValidateTokenForAccountMigratinQuery();

  const [
    verifyEmail,
    {
      isLoading: isVerifyEmailLoading,
      isError: isVerifyEmailError,
      isSuccess: isVerifyEmailSuccess,
      error: verifyEmailError,
    },
  ] = useVerifyEmailMutation();

  const handleTokenError = useCallback(() => {
    const requestError = (error as FetchBaseQueryError).data as IError;
    const { email } = requestError;

    const errorMessage = requestError.message || requestError.Message;

    const isTokenExpired = errorMessage
      ?.toLowerCase()
      .includes('token expired');
    const isTokenInvalid = errorMessage
      ?.toLowerCase()
      .includes('invalid token');

    navigate(`/tokenerror/${email || 'invalid-email'}`, {
      state: {
        errorMessage,
        isTokenExpired,
        isTokenInvalid,
      },
    });
  }, [error]);

  const finishRegistration = (agentData: IProfileData) => {
    const registrationData = {
      ...agentData,
      email: validatedEmail,
      password,
    };

    dispatch(registration(registrationData, true));
  };

  const isLoading =
    token &&
    (isFetching ||
      isVerifyEmailLoading ||
      !(isVerifyEmailError || isVerifyEmailSuccess));

  useEffect(() => {
    if (pathname === '/accountmigration') {
      const email = state as SetStateAction<string>;

      setValidatedEmail(email);
      dispatch(setLoginStatus(LOGIN_STATUSES.WAIT_EMAIL_VERIFICATION));
    }

    if (token) {
      validateTokenForAccountMigration({ token });
    }

    return () => {
      dispatch(resetAuthError());
    };
  }, []);

  useEffect(() => {
    if (isError) {
      handleTokenError();
      dispatch(
        createFailedMessage(
          translate(
            'Login_ErrorMessage_SomethingWentWronInAccountMigrationProcess'
          )
        )
      );
    }
  }, [isError]);

  useEffect(() => {
    if (isAuthenticated) {
      if (process.env.REACT_APP_COMPANY === 'HRN') {
        navigate('/book/coastal');
      } else {
        navigate('/book/expedition');
      }
    }

    if (registrationError) {
      dispatch(setLoginStatus(LOGIN_STATUSES.ACCOUNT_AGENT_INFO));
      dispatch(
        createFailedMessage(
          translate(
            'Login_ErrorMessage_UnfortunatelyWeCannotCreate',
            supportEmail
          )
        )
      );
    }
  }, [isAuthenticated, registrationError]);

  useEffect(() => {
    if (isSuccess) {
      const onVerifyEmail = async () => {
        await verifyEmail({ email: data[0].email });
      };

      onVerifyEmail();
      setValidatedEmail(data[0].email);
      dispatch(setLoginStatus(LOGIN_STATUSES.ACCOUNT_MIGRATION_PASSWORD));
    }
  }, [isSuccess]);

  useEffect(() => {
    if (registrationResponse !== null) {
      const authData: IAuthPayload = {
        email: registrationResponse.email,
        password,
      };

      auth({ authData });
    }
  }, [registrationResponse?.email]);

  if (isVerifyEmailSuccess) {
    navigate('/signupfinish');
  }

  if (isVerifyEmailError) {
    const responseError = (verifyEmailError as FetchBaseQueryError)
      .data as IError;
    if (responseError?.errorCode !== '404A') {
      navigate(`/tokenerror/${validatedEmail || 'invalid-email'}`, {
        state: {
          errorMessage: error ? responseError.message : '',
        },
      });
    }
  }

  const renderLoginState = () => {
    window.scrollTo(0, 0);
    switch (loginStatus) {
      case LOGIN_STATUSES.WAIT_USER_EMAIL:
        return (
          <LoginWaitUserEmail
            setLoginState={(loginState, _email) => {
              dispatch(setLoginStatus(loginState));
              setValidatedEmail(_email);
            }}
          />
        );
      case LOGIN_STATUSES.WAIT_EMAIL_VERIFICATION:
        return <LoginWaitEmailVerification email={validatedEmail} />;
      case LOGIN_STATUSES.ACCOUNT_MIGRATION_PASSWORD:
        return (
          <LoginAccountMigrationPassword
            validatedEmail={validatedEmail}
            onSubmit={(_password) => {
              dispatch(setLoginStatus(LOGIN_STATUSES.ACCOUNT_AGENT_INFO));
              setPassword(_password);
            }}
          />
        );
      case LOGIN_STATUSES.ACCOUNT_AGENT_INFO:
        return (
          <LoginAccountAgentInfo
            contacts={data}
            onSubmit={(agentData) => {
              dispatch(setLoginStatus(LOGIN_STATUSES.FINISH_LOGIN));
              finishRegistration(agentData);
            }}
          />
        );
      case LOGIN_STATUSES.FINISH_LOGIN:
      default:
        return <LoadingGif />;
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <LoadingGif dataTestid="login-loading" />
      </div>
    );
  }
  return renderLoginState();
};

export default Login;
